/**
 * CalendarSettings.js
 * /settings/calendar — Full calendar integration management page.
 * Accessible only to authenticated users.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Grid, Alert, Snackbar, Divider, Paper,
    Button, CircularProgress
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SyncIcon from '@mui/icons-material/Sync';
import Header from '../../components/header';
import SideNavbar from '../../components/sideNavbar';
import CalendarConnectionCard from '../appointment/CalendarConnectionCard';
import axiosInstance from '../../services/axiosInstance';

const CalendarSettings = () => {
    const [statuses, setStatuses] = useState({
        google: { isConnected: false, email: null, syncEnabled: false, lastSyncedAt: null },
        outlook: { isConnected: false, email: null, syncEnabled: false, lastSyncedAt: null },
    });
    const [loading, setLoading] = useState(true);
    const [connecting, setConnecting] = useState({ google: false, outlook: false });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const showSnack = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchStatuses = useCallback(async () => {
        const userId = localStorage.getItem('userId');
        if (!userId) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get('/calendar-accounts/status', { params: { userId } });
            if (res.data.success) {
                setStatuses({
                    google: res.data.google || statuses.google,
                    outlook: res.data.outlook || statuses.outlook,
                });
            }
        } catch (err) {
            console.error('Failed to fetch calendar statuses:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatuses();
    }, [fetchStatuses]);

    const handleConnectGoogle = async () => {
        setConnecting(prev => ({ ...prev, google: true }));
        try {
            const res = await axiosInstance.get('/calendar/auth-url');
            if (res.data.success) {
                window.location.href = res.data.url;
            }
        } catch (err) {
            showSnack('Failed to start Google authorization', 'error');
            setConnecting(prev => ({ ...prev, google: false }));
        }
    };

    const handleConnectOutlook = async () => {
        setConnecting(prev => ({ ...prev, outlook: true }));
        try {
            const res = await axiosInstance.get('/outlook-calendar/auth-url');
            if (res.data.success) {
                window.location.href = res.data.url;
            }
        } catch (err) {
            showSnack('Failed to start Outlook authorization', 'error');
            setConnecting(prev => ({ ...prev, outlook: false }));
        }
    };

    const handleDisconnectGoogle = async () => {
        const userId = localStorage.getItem('userId');
        try {
            const res = await axiosInstance.post('/calendar/disconnect', { userId });
            if (res.data.success) {
                showSnack('Google Calendar disconnected');
                fetchStatuses();
            }
        } catch (err) {
            showSnack('Failed to disconnect Google Calendar', 'error');
        }
    };

    const handleDisconnectOutlook = async () => {
        const userId = localStorage.getItem('userId');
        try {
            const res = await axiosInstance.post('/outlook-calendar/disconnect', { userId });
            if (res.data.success) {
                showSnack('Outlook Calendar disconnected');
                fetchStatuses();
            }
        } catch (err) {
            showSnack('Failed to disconnect Outlook Calendar', 'error');
        }
    };

    const handleToggleSync = async (provider, syncEnabled) => {
        const userId = localStorage.getItem('userId');
        try {
            await axiosInstance.post('/calendar-accounts/toggle-sync', { userId, provider, syncEnabled });
            setStatuses(prev => ({
                ...prev,
                [provider]: { ...prev[provider], syncEnabled },
            }));
            showSnack(`${provider === 'google' ? 'Google' : 'Outlook'} auto-sync ${syncEnabled ? 'enabled' : 'disabled'}`);
        } catch (err) {
            showSnack('Failed to update sync setting', 'error');
        }
    };

    return (
        <Box>
            <Box sx={{ ml: { xs: '0px', md: '280px' } }}>
                <SideNavbar />
                <Box sx={{ display: { xs: 'none', md: 'initial' } }}>
                    <Header title="Calendar Settings" />
                </Box>

                <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
                    {/* Page Header */}
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Box sx={{
                                width: 48, height: 48, borderRadius: 3,
                                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 4px 14px #6366f144',
                            }}>
                                <CalendarMonthIcon sx={{ color: 'white', fontSize: 24 }} />
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight={800} color="#0f172a">
                                    Calendar Integrations
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Connect your calendar accounts to sync appointments automatically
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Info Banner */}
                    <Alert
                        severity="info"
                        icon={<SyncIcon />}
                        sx={{ mb: 3, borderRadius: 2, border: '1px solid #bae6fd' }}
                    >
                        <strong>Two-Way Sync:</strong> When connected, appointments created in JustTawk CRM appear in your calendar,
                        and events added externally appear in your CRM. Double-bookings are prevented automatically.
                    </Alert>

                    {/* Connection Cards */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <CalendarConnectionCard
                                    provider="google"
                                    isConnected={statuses.google.isConnected}
                                    email={statuses.google.email}
                                    syncEnabled={statuses.google.syncEnabled}
                                    lastSyncedAt={statuses.google.lastSyncedAt}
                                    loading={connecting.google}
                                    onConnect={handleConnectGoogle}
                                    onDisconnect={handleDisconnectGoogle}
                                    onToggleSync={(val) => handleToggleSync('google', val)}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CalendarConnectionCard
                                    provider="outlook"
                                    isConnected={statuses.outlook.isConnected}
                                    email={statuses.outlook.email}
                                    syncEnabled={statuses.outlook.syncEnabled}
                                    lastSyncedAt={statuses.outlook.lastSyncedAt}
                                    loading={connecting.outlook}
                                    onConnect={handleConnectOutlook}
                                    onDisconnect={handleDisconnectOutlook}
                                    onToggleSync={(val) => handleToggleSync('outlook', val)}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {/* How It Works */}
                    <Paper elevation={0} sx={{ mt: 4, p: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#fafafa' }}>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                            📖 How Sync Works
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            {[
                                { icon: '📅', title: 'CRM → Calendar', text: 'New appointments automatically appear in your connected calendar with Meet/Teams links.' },
                                { icon: '🔄', title: 'Calendar → CRM', text: 'Events created externally in Google or Outlook auto-create CRM appointments.' },
                                { icon: '❌', title: 'Cancellations', text: 'Cancelling in either direction removes the event from both systems.' },
                                { icon: '🔐', title: 'Security', text: 'OAuth tokens are encrypted at rest. Your credentials are never stored in plaintext.' },
                            ].map((item, i) => (
                                <Grid item xs={12} sm={6} key={i}>
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        <Typography sx={{ fontSize: 20 }}>{item.icon}</Typography>
                                        <Box>
                                            <Typography variant="body2" fontWeight={700}>{item.title}</Typography>
                                            <Typography variant="caption" color="text.secondary">{item.text}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Box>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CalendarSettings;
